import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
} from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgIcon } from '@ng-icons/core';
import type { GetBuildingDocumentStorageUsageResponse } from '@quipex/shared/contracts';
import { QpxIcon, QpxTypography, QpxTooltip } from '@quipex/shared/ui';
import { formatDocumentStorageBytes } from './document-storage-capacity-status';

@Component({
    selector: 'qpx-document-storage-usage',
    imports: [
        MatProgressBarModule,
        MatProgressSpinnerModule,
        QpxTooltip,
        NgIcon,
        QpxIcon,
        QpxTypography,
    ],
    templateUrl: './document-storage-usage.html',
    styleUrl: './document-storage-usage.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.primary]': "context() === 'primary'",
        '[class.destructive]': "context() === 'destructive'",
    },
})
export class DocumentStorageUsage {
    public readonly context = input<'caution' | 'primary' | 'destructive'>(
        'caution'
    );
    public readonly label = input.required<string>();
    public readonly mini = input(false);
    public readonly storageUsage =
        input<GetBuildingDocumentStorageUsageResponse>();
    public readonly storageUsageFailed = input(false);
    public readonly storageUsageLoading = input(false);

    protected readonly storageLabel = computed(() => {
        const usage = this.storageUsage();
        if (!usage) return '';

        return `${formatDocumentStorageBytes(usage.usedBytes)} of ${formatDocumentStorageBytes(usage.limitBytes)} used`;
    });
    protected readonly progressPercentage = computed(() => {
        const usage = this.storageUsage();
        if (!usage) return 0;

        const limit = Math.max(usage.limitBytes, 1);
        return Math.min(Math.max((usage.usedBytes / limit) * 100, 0), 100);
    });
    protected readonly percentageLabel = computed(() => {
        const usage = this.storageUsage();
        if (!usage || usage.limitBytes <= 0) return 'Storage usage unavailable';
        return `${Math.round((Math.max(usage.usedBytes, 0) / usage.limitBytes) * 100)}% storage used`;
    });
    protected readonly miniTooltip = computed(() => {
        if (this.storageUsageLoading()) return 'Loading storage usage';
        if (this.storageUsage()) return this.storageLabel();
        return 'Storage usage is temporarily unavailable. The effective storage limit still applies.';
    });
}
